/**
 * EditorHeader — Scenario/sensitivity selectors, rename inputs, ERP sync button.
 * Extracted from FinancialModelEditor.tsx lines ~2041–2235 (Phase 3A).
 */

import React, { useMemo, useCallback } from 'react';
import TutorialStep from '../../../components/Tutorial';
import { FMEPageHeader } from '../../../components/Layout';
import { TailwindSelect } from '../../../components/Tailwind';
import { useChatOpen, useSharedChat } from '../../../hooks/useAgentContext';

interface EditorHeaderProps {
    model: any;
    currentScenario: any;
    currentSensitivity: any;
    viewMode: 'overview' | 'section';
    currentSection: any;
    userRights: any;
    hasErpConnections: boolean;
    // Rename state
    renamingScenario: boolean;
    setRenamingScenario: (v: boolean) => void;
    scenarioRenameValue: string;
    setScenarioRenameValue: (v: string) => void;
    renamingSensitivity: boolean;
    setRenamingSensitivity: (v: boolean) => void;
    sensitivityRenameValue: string;
    setSensitivityRenameValue: (v: string) => void;
    // Handlers
    handleRenameScenario: () => void;
    handleRenameSensitivity: () => void;
    startRenamingScenario: () => void;
    startRenamingSensitivity: () => void;
    onScenarioChange: (scenario: any) => void;
    onSensitivityChange: (sensitivity: any) => void;
    onNewScenario: () => void;
    onNewSensitivity: () => void;
    onDeleteScenario: () => void;
    onDeleteSensitivity: () => void;
    onErpSync: () => void;
    onBreadcrumbClick?: (index: number) => void;
    setModelDataSelected: (item: any) => void;
    setCurrentSensitivity: React.Dispatch<any>;
}

export default function EditorHeader({
    model,
    currentScenario,
    currentSensitivity,
    viewMode,
    currentSection,
    userRights,
    hasErpConnections,
    renamingScenario,
    setRenamingScenario,
    scenarioRenameValue,
    setScenarioRenameValue,
    renamingSensitivity,
    setRenamingSensitivity,
    sensitivityRenameValue,
    setSensitivityRenameValue,
    handleRenameScenario,
    handleRenameSensitivity,
    startRenamingScenario,
    startRenamingSensitivity,
    onScenarioChange,
    onSensitivityChange,
    onNewScenario,
    onNewSensitivity,
    onDeleteScenario,
    onDeleteSensitivity,
    onErpSync,
    onBreadcrumbClick,
    setModelDataSelected,
    setCurrentSensitivity,
}: EditorHeaderProps) {
    const { setIsChatOpen } = useChatOpen();
    const { sendMessage } = useSharedChat();

    const handleSensitivityInfo = useCallback(() => {
        setIsChatOpen(true);
        sendMessage("Explain me what is a Sensitivity in Peerception and how to use it ?");
    }, [setIsChatOpen, sendMessage]);

    const headerActions = useMemo(() => (
        <div className="flex items-center gap-2 flex-wrap">
            {/* Scenario Selector */}
            <TutorialStep tutorial='main' identifier='scenario_selector' display='inline-block' position="none" marginTop={10}>
                <div className="flex flex-col">
                    <div className="min-w-[200px]">
                        {renamingScenario ? (
                            <div className="flex items-center gap-1 mt-4">
                                <input
                                    autoFocus
                                    type="text"
                                    value={scenarioRenameValue}
                                    onChange={(e) => setScenarioRenameValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleRenameScenario();
                                        if (e.key === 'Escape') setRenamingScenario(false);
                                    }}
                                    onBlur={handleRenameScenario}
                                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-slate-900"
                                />
                            </div>
                        ) : (
                            <TailwindSelect
                                label="Scenario"
                                value={currentScenario?.id?.toString() ?? ''}
                                options={model.modelScenarios.map((s: any) => ({
                                    value: s.id.toString(),
                                    label: s.name
                                }))}
                                onChange={(value: string) => {
                                    const scenario = model.modelScenarios.find((s: any) => s.id.toString() === value);
                                    if (scenario) {
                                        onScenarioChange(scenario);
                                    }
                                }}
                            />
                        )}
                    </div>
                    {userRights.right_to_write && (
                        <div className="flex items-center gap-0.5 mt-1">
                            <button
                                onClick={onNewScenario}
                                disabled={model.modelRulesDeducted.number_scenario_max_per_model !== -1 && model.modelRulesDeducted.number_scenario_max_per_model - model.modelScenarios.length <= 0}
                                className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title={model.modelRulesDeducted.number_scenario_max_per_model !== -1 && model.modelRulesDeducted.number_scenario_max_per_model - model.modelScenarios.length <= 0 ? `Maximum scenarios reached (${model.modelRulesDeducted.number_scenario_max_per_model})` : 'New scenario'}
                            >
                                <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">add</span>
                            </button>
                            <button
                                onClick={startRenamingScenario}
                                className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors"
                                title="Rename scenario"
                            >
                                <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">edit</span>
                            </button>
                            {model.modelScenarios.length > 1 && (
                                <button
                                    onClick={onDeleteScenario}
                                    className="flex items-center justify-center p-0.5 rounded hover:bg-red-50 transition-colors"
                                    title="Delete scenario"
                                >
                                    <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-red-500">delete</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </TutorialStep>

            {/* Sensitivity Selector */}
            <TutorialStep tutorial='main' identifier='sensitivity_selector' display='inline-block' position="none" marginTop={10} marginLeft={-100}>
                <div className="flex flex-col">
                    <div className="min-w-[200px]">
                        {renamingSensitivity && currentSensitivity !== 'null' ? (
                            <div className="flex items-center gap-1 mt-4">
                                <input
                                    autoFocus
                                    type="text"
                                    value={sensitivityRenameValue}
                                    onChange={(e) => setSensitivityRenameValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleRenameSensitivity();
                                        if (e.key === 'Escape') setRenamingSensitivity(false);
                                    }}
                                    onBlur={handleRenameSensitivity}
                                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-slate-900"
                                />
                            </div>
                        ) : (
                            <TailwindSelect
                                label="Sensitivity"
                                value={currentSensitivity === 'null' ? 'null' : currentSensitivity.id.toString()}
                                options={[
                                    { value: 'null', label: 'No Sensitivity' },
                                    ...(currentScenario?.sensitivities ?? []).map((s: any) => ({
                                        value: s.id.toString(),
                                        label: s.name
                                    }))
                                ]}
                                onChange={(value: string) => {
                                    if (value === 'null') {
                                        setModelDataSelected(null);
                                        setCurrentSensitivity('null');
                                    } else {
                                        const sensitivity = (currentScenario?.sensitivities ?? []).find((s: any) => s.id.toString() === value);
                                        if (sensitivity) {
                                            onSensitivityChange(sensitivity);
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                    <div className="flex items-center gap-0.5 mt-1">
                        {userRights.right_to_write && (
                            <>
                                <button
                                    onClick={onNewSensitivity}
                                    disabled={model.modelRulesDeducted.number_sensitivities_max_per_scenario !== -1 && model.modelRulesDeducted.number_sensitivities_max_per_scenario - (currentScenario?.sensitivities?.length ?? 0) <= 0}
                                    className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    title={model.modelRulesDeducted.number_sensitivities_max_per_scenario !== -1 && model.modelRulesDeducted.number_sensitivities_max_per_scenario - (currentScenario?.sensitivities?.length ?? 0) <= 0 ? `Maximum sensitivities reached (${model.modelRulesDeducted.number_sensitivities_max_per_scenario})` : 'New sensitivity'}
                                >
                                    <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">add</span>
                                </button>
                                {currentSensitivity !== 'null' && (
                                    <button
                                        onClick={startRenamingSensitivity}
                                        className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors"
                                        title="Rename sensitivity"
                                    >
                                        <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">edit</span>
                                    </button>
                                )}
                                {currentSensitivity !== 'null' && (
                                    <button
                                        onClick={onDeleteSensitivity}
                                        className="flex items-center justify-center p-0.5 rounded hover:bg-red-50 transition-colors"
                                        title="Delete sensitivity"
                                    >
                                        <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-red-500">delete</span>
                                    </button>
                                )}
                            </>
                        )}
                        <button
                            onClick={handleSensitivityInfo}
                            className="flex items-center justify-center p-0.5 rounded hover:bg-slate-100 transition-colors"
                            title="What is a sensitivity?"
                        >
                            <span className="material-symbols-outlined text-[16px] leading-none text-slate-400 hover:text-slate-600">help_outline</span>
                        </button>
                    </div>
                </div>
            </TutorialStep>
        </div>
    ), [currentScenario, currentSensitivity, renamingScenario, scenarioRenameValue, renamingSensitivity, sensitivityRenameValue, userRights, model.modelScenarios, model.modelRulesDeducted, hasErpConnections, handleSensitivityInfo]);

    return (
        <FMEPageHeader
            title="Financial Model Editor"
            breadcrumb={
                viewMode === 'section' && currentSection
                    ? [model.name, currentSection.config.name]
                    : [model.name]
            }
            onBreadcrumbClick={viewMode === 'section' ? onBreadcrumbClick : undefined}
            description={
                viewMode === 'overview'
                    ? 'Build and organize your financial model structure, inputs, and assumptions.'
                    : currentSection?.config.description
            }
            actions={headerActions}
            titleExtra={hasErpConnections && currentSensitivity === 'null' ? (
                <button
                    onClick={onErpSync}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:border-slate-300 hover:bg-slate-50 transition-colors"
                >
                    <span className="material-symbols-outlined text-[16px]">sync</span>
                    ERP Sync
                </button>
            ) : undefined}
        />
    );
}
